import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Button,
  TextField,
  Chip,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Badge,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Snackbar
} from '@mui/material';
import {
  Person,
  Edit,
  Save,
  Cancel,
  PhotoCamera,
  Email,
  Phone,
  LocationOn,
  Cake,
  WorkspacePremium,
  Timeline,
  TrendingUp,
  MonitorHeart,
  Assessment,
  CalendarToday,
  Shield,
  Verified,
  Settings,
  Download,
  Share,
  Delete,
  Add,
  Check,
  Warning
} from '@mui/icons-material';
import { updateUser } from '../store/authSlice';

const ProfilePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isLoading } = useSelector((state) => state.auth);
  
  const [editing, setEditing] = useState(false);
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    age: '',
    gender: '',
    phone: '',
    address: '',
    bio: '',
    medicalHistory: '',
    emergencyContact: '',
    occupation: '',
    dateOfBirth: ''
  });

  const [profileStats, setProfileStats] = useState({
    totalReadings: 42,
    avgSystolic: 122,
    avgDiastolic: 78,
    lastReading: '2025-01-08',
    accountAge: 30,
    streak: 7
  });

  const [achievements, setAchievements] = useState([
    { id: 1, title: 'First Reading', description: 'Recorded your first blood pressure reading', earned: true },
    { id: 2, title: 'Week Streak', description: 'Maintained readings for 7 consecutive days', earned: true },
    { id: 3, title: 'Healthy Range', description: 'Maintained healthy BP range for a month', earned: false },
    { id: 4, title: 'Data Export', description: 'Exported your health data', earned: false }
  ]);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        age: user.age || '',
        gender: user.gender || '',
        phone: user.phone || '',
        address: user.address || '',
        bio: user.bio || '',
        medicalHistory: user.medicalHistory || '',
        emergencyContact: user.emergencyContact || '',
        occupation: user.occupation || '',
        dateOfBirth: user.dateOfBirth || ''
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSaveProfile = async () => {
    try {
      await dispatch(updateUser(formData));
      setSuccessMessage('Profile updated successfully!');
      setEditing(false);
    } catch (error) {
      setErrorMessage('Failed to update profile. Please try again.');
    }
  };

  const handleAvatarUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Here you would typically upload the file to a server
      // For now, we'll just simulate the upload
      setSuccessMessage('Avatar updated successfully!');
      setAvatarDialogOpen(false);
    }
  };

  const getHealthStatus = () => {
    const { avgSystolic, avgDiastolic } = profileStats;
    if (avgSystolic < 120 && avgDiastolic < 80) return { status: 'Excellent', color: 'success' };
    if (avgSystolic < 130 && avgDiastolic < 80) return { status: 'Good', color: 'warning' };
    return { status: 'Monitor', color: 'error' };
  };

  const healthStatus = getHealthStatus();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
          My Profile
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            onClick={() => navigate('/settings')}
          >
            Settings
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => navigate('/export')}
          >
            Export Data
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Profile Header */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    <Tooltip title="Change Avatar">
                      <IconButton
                        size="small"
                        onClick={() => setAvatarDialogOpen(true)}
                        sx={{
                          bgcolor: 'white',
                          color: 'primary.main',
                          width: 32,
                          height: 32,
                          '&:hover': { bgcolor: 'grey.100' }
                        }}
                      >
                        <PhotoCamera fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      border: '4px solid white',
                      cursor: 'pointer'
                    }}
                    onClick={() => setAvatarDialogOpen(true)}
                  >
                    <Person sx={{ fontSize: 60 }} />
                  </Avatar>
                </Badge>
              </Grid>
              <Grid item xs>
                <Typography variant="h4" gutterBottom>
                  {user?.firstName || 'User'} {user?.lastName || ''}
                  <Verified sx={{ ml: 1, verticalAlign: 'middle' }} />
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }} gutterBottom>
                  {user?.email}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={`${profileStats.accountAge} days active`}
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                    size="small"
                  />
                  <Chip
                    label={`${profileStats.streak} day streak`}
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                    size="small"
                  />
                  <Chip
                    label={`${profileStats.totalReadings} readings`}
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                    size="small"
                  />
                </Stack>
              </Grid>
              <Grid item>
                <Box textAlign="center">
                  <Typography variant="h6" gutterBottom>
                    Health Status
                  </Typography>
                  <Chip
                    label={healthStatus.status}
                    color={healthStatus.color}
                    size="large"
                    sx={{ fontSize: '1rem', py: 1 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Profile Information */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6">Profile Information</Typography>
              <Button
                variant={editing ? 'outlined' : 'contained'}
                startIcon={editing ? <Cancel /> : <Edit />}
                onClick={() => {
                  if (editing) {
                    setFormData({
                      firstName: user?.firstName || '',
                      lastName: user?.lastName || '',
                      email: user?.email || '',
                      age: user?.age || '',
                      gender: user?.gender || '',
                      phone: user?.phone || '',
                      address: user?.address || '',
                      bio: user?.bio || '',
                      medicalHistory: user?.medicalHistory || '',
                      emergencyContact: user?.emergencyContact || '',
                      occupation: user?.occupation || '',
                      dateOfBirth: user?.dateOfBirth || ''
                    });
                  }
                  setEditing(!editing);
                }}
              >
                {editing ? 'Cancel' : 'Edit Profile'}
              </Button>
            </Box>

            {editing ? (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Age"
                    name="age"
                    type="number"
                    value={formData.age}
                    onChange={handleInputChange}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Cake />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Gender</InputLabel>
                    <Select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      label="Gender"
                    >
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                      <MenuItem value="prefer-not-to-say">Prefer not to say</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    variant="outlined"
                    multiline
                    rows={2}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOn />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Bio"
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    variant="outlined"
                    multiline
                    rows={3}
                    placeholder="Tell us about yourself..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Emergency Contact"
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleInputChange}
                    variant="outlined"
                    placeholder="Name and phone number"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" gap={2} mt={2}>
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={handleSaveProfile}
                      disabled={isLoading}
                    >
                      Save Changes
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={() => setEditing(false)}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <List>
                <ListItem>
                  <ListItemIcon><Person /></ListItemIcon>
                  <ListItemText
                    primary="Full Name"
                    secondary={`${user?.firstName || ''} ${user?.lastName || ''}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Email /></ListItemIcon>
                  <ListItemText
                    primary="Email"
                    secondary={user?.email || 'Not provided'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Cake /></ListItemIcon>
                  <ListItemText
                    primary="Age"
                    secondary={user?.age || 'Not provided'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Phone /></ListItemIcon>
                  <ListItemText
                    primary="Phone"
                    secondary={user?.phone || 'Not provided'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><LocationOn /></ListItemIcon>
                  <ListItemText
                    primary="Address"
                    secondary={user?.address || 'Not provided'}
                  />
                </ListItem>
                {user?.bio && (
                  <ListItem>
                    <ListItemIcon><Person /></ListItemIcon>
                    <ListItemText
                      primary="Bio"
                      secondary={user.bio}
                    />
                  </ListItem>
                )}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Health Stats & Achievements */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            {/* Health Stats */}
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <MonitorHeart sx={{ mr: 1, verticalAlign: 'middle' }} />
                Health Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {profileStats.avgSystolic}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg Systolic
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="secondary">
                      {profileStats.avgDiastolic}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg Diastolic
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="success.main">
                      {profileStats.totalReadings}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Readings
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="warning.main">
                      {profileStats.streak}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Day Streak
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Assessment />}
                sx={{ mt: 2 }}
                onClick={() => navigate('/analytics')}
              >
                View Analytics
              </Button>
            </Paper>

            {/* Achievements */}
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <WorkspacePremium sx={{ mr: 1, verticalAlign: 'middle' }} />
                Achievements
              </Typography>
              <Stack spacing={2}>
                {achievements.map((achievement) => (
                  <Box key={achievement.id} display="flex" alignItems="center">
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        mr: 2,
                        bgcolor: achievement.earned ? 'success.main' : 'grey.300'
                      }}
                    >
                      {achievement.earned ? <Check /> : <Shield />}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2">
                        {achievement.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {achievement.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      {/* Avatar Upload Dialog */}
      <Dialog
        open={avatarDialogOpen}
        onClose={() => setAvatarDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Profile Picture</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
            <Avatar sx={{ width: 120, height: 120 }}>
              <Person sx={{ fontSize: 60 }} />
            </Avatar>
            <Button
              variant="contained"
              component="label"
              startIcon={<PhotoCamera />}
            >
              Choose Photo
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleAvatarUpload}
              />
            </Button>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Choose a JPG, PNG, or GIF file smaller than 5MB
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvatarDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProfilePage; 